import { useQuery } from '@apollo/client';
import { tracking } from 'App/Tracking';
import { LINE_MANAGERS_CATEGORY } from 'Managers/constants';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { useUserEntitlements } from 'hooks/useUserEntitlements';
import RoutePath from '../../../App/RoutePath';
import GenericExploreContent from '../../../Explore/GenericExploreContent/GenericExploreContent';
import SectionError from '../../../Shared/SectionError';
import HomeSectionSkeleton from '../components/HomeSectionSkeleton';
import { useAssetToken } from '../hooks/useAssetToken';
import { useSectionErrorLogging } from '../hooks/useSectionErrorLogging';
import { HOME_SECTION_QUERY_OPTIONS } from '../sectionQueryOptions';
import {
  HomeForManagers,
  HomeForManagersVariables,
} from './__generated__/HomeForManagers';
import { HOME_FOR_MANAGERS_QUERY } from './forManagersQueries';
import { selectManagerContent } from './selectManagerContent';

const ForManagers = () => {
  const { t: translate } = useTranslation('homepage');
  const { entitlements, loading: isEntitlementsLoading } =
    useUserEntitlements();
  const { assetToken } = useAssetToken();

  const isManager = entitlements.lineManagerContent;

  const { data, loading, error, refetch } = useQuery<
    HomeForManagers,
    HomeForManagersVariables
  >(HOME_FOR_MANAGERS_QUERY, {
    ...HOME_SECTION_QUERY_OPTIONS,
    variables: { slug: LINE_MANAGERS_CATEGORY },
    skip: !isManager,
  });

  useSectionErrorLogging(error, data, 'for-managers');

  // Nothing rather than a skeleton while entitlements load: most users are not
  // managers, so a skeleton would flash a section that then vanishes.
  if (isEntitlementsLoading || !isManager) {
    return null;
  }

  if (loading && !data) {
    return <HomeSectionSkeleton testId="for-managers-skeleton" tileCount={3} />;
  }

  if (error && !data) {
    // `errorPolicy: 'none'` makes refetch() reject on failure; the hook's own
    // `data` and `error` still drive what renders.
    return (
      <SectionError onRetry={async () => refetch().catch(() => undefined)} />
    );
  }

  const content = selectManagerContent(data);

  if (!content.length) {
    return null;
  }

  return (
    <section>
      <div className="home-section-heading">
        <h2 className="title-s section-title">
          {translate('for_managers_section.title')}
        </h2>
        <Link
          className="link"
          to={RoutePath.Managers}
          onClick={() =>
            tracking.track('homepage-line-manager-content-view-clicked')
          }
          aria-label={translate('for_managers_section.arrow_link_button.text')}
        >
          {translate('for_managers_section.arrow_link_button.text')}
        </Link>
      </div>
      <GenericExploreContent
        content={content}
        assetToken={assetToken}
        source="home-for-managers"
      />
    </section>
  );
};

export default ForManagers;
